<template>
  <div class="step-content">
    <completion-progress
      v-if="actionRequired&&!loading"
      :completion="completion"
      class="px-8"
    />

    <v-card 
      outlined
      color="transparent"
      class="checklist text-left mx-n4 my-10"
    >
      <v-progress-linear
        :active="loading"
        indeterminate
        color="secondary"
        class="mb-0"
      />
      <v-list 
        v-show="!loading"
        two-line
        color="transparent"
        class="feedbacks py-0 mt-0"
      >
        <v-list-item
          class="item"
          @click="toggleView('profile')"
        >
          <v-list-item-content>
            <v-list-item-title
              class="item-title"
            >
              {{ $t('signup.steps.ENR.profile_info') }}
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action
            class="item-action"
          >
            <v-btn 
              v-if="user.status=='FDD'"
              icon
              color="primary"
              @click.stop="toggleView('profile')"
            >
              <v-icon>{{ icons.mdiFileSearchOutline }}</v-icon>
            </v-btn>
            <v-btn 
              v-else-if="step.profile<1"
              text
              color="primary"
              class="mr-n2"
              @click="toggleView('profile')"
            >
              {{ $t('general.fill_out') }}
            </v-btn>
            <v-btn 
              v-else
              icon
              color="primary"
              @click.stop="toggleView('profile')"
            >
              <v-icon>{{ icons.mdiCheck }}</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
        <v-list-item
          v-for="item in step.docs.items"
          :key="item.criteria"
          class="item"
          @click="toggleDoc(true, item.criteria)"
        >
          <v-list-item-content>
            <v-list-item-title
              class="item-title"
            >
              {{ decodeWeek($t(getDocInfo(item.criteria).title)) }}
            </v-list-item-title>
            <v-list-item-subtitle
              v-if="item.status==3"
              class="item-feedback mt-2"
            >
              ⚠️ {{ item.feedback }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action
            class="item-action"
          >
            <v-btn 
              v-if="user.status=='FDD'"
              icon
              color="primary"
              :loading="docs[item.criteria].loading"
              @click.stop="toggleDoc(true, item.criteria)"
            >
              <v-icon>{{ icons.mdiFileSearchOutline }}</v-icon>
            </v-btn>
            <v-btn 
              v-else-if="docPending(item.status)&&!docs[item.criteria].loading"
              text
              color="primary"
              class="mr-n2"
              @click.stop="toggleDoc(true, item.criteria)"
            >
              {{ $t('general.send') }}
            </v-btn>
            <v-btn 
              v-else
              icon
              color="primary"
              :loading="docs[item.criteria].loading"
              @click.stop="toggleDoc(true, item.criteria)"
            >
              <v-icon>{{ icons.mdiCheck }}</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card>

    <referral-card 
      :user="user"
      @help="toggleHelp"
    />

    <slot name="quick-access" />

    <v-btn 
      text
      small
      class="help-btn"
      color="grey"
      @click="toggleHelp(true, 'signup')"
    >
      <v-icon
        small
        class="mr-4"
      >
        {{ icons.mdiHelpCircleOutline }}
      </v-icon>
      {{ $t('general.need_help') }}
    </v-btn>

    <doc-view
      :toggle="docView.toggle"
      :loading="docView.loading"
      :doc="getDocInfo(docView.selected)"
      :status="user.status"
      @send="sendDoc"
      @close="toggleDoc(false)"
    />


    <v-dialog
      v-model="confirm.toggle"
      overlay-opacity=".8"
      transition="slide-y-reverse-transition"
    >
      <v-card color="black">
        <v-card-title>{{ $t('signup.steps.ENR.confirm.title') }}</v-card-title>
        <v-card-text class="py-4">
          <p 
            class="text-body-1 font-weight-bold"
            v-html="formatText($t('signup.steps.ENR.confirm.text'))"
          />
        </v-card-text>

        <v-card-actions class="pb-4">
          <v-btn
            color="grey"
            text
            :disabled="confirm.loading"
            @click="toggleConfirm(false);"
          >
            {{ $t('general.back') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            text
            :loading="confirm.loading"
            @click="requestDocEvaluation"
          >
            {{ $t('general.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style>

  .step-content .checklist {
    max-width: initial;
    border-color: var(--v-grey-darken3) !important;
  }

  .step-content .checklist .item-feedback {
    line-height: 1.4;
    text-overflow: initial;
    white-space: initial;
  }

  .step-content .checklist .deadline {
    margin: -1px;
  }

  .step-content .checklist .item:not(:last-child) {
    border-bottom: 1px solid var(--v-grey-darken3) !important;
  }

</style>

<script>

  // Icons
  import {
    mdiHelpCircleOutline,
    mdiClose,
    mdiCalendarAlert,
    mdiCheck,
    mdiFileSearchOutline
  } from '@mdi/js'

  // Utilities
  import services from '@/services.js'
  import { docs } from '@/services/signup'
  import { sync, get } from 'vuex-pathify'
  import _ from 'lodash';


  export default {
    name: 'Step3',

    props: {
      step: {
        type: Object,
        default: () => {}
      },
      appURL: {
        type: String,
        default: ''
      },
      refresh: {
        type: Boolean,
        default: false
      }
    },

    components: {
      ReferralCard: () => import('@/components/ReferralCard'),
      CompletionProgress: () => import('@/components/CompletionProgress'),
      DocView: () => import('@/components/DocView'),
    },

    data: () => ({
      api: process.env.VUE_APP_ROOT_API,
      icons: {
        mdiHelpCircleOutline,
        mdiClose,
        mdiCalendarAlert,
        mdiCheck,
        mdiFileSearchOutline
      },
      docView: {
        toggle: false,
        selected: null,
        loading: false,
      },
      confirm: {
        toggle: false,
        loading: false
      },
      btnSend: {
        loading: false,
        disabled: false,
      },
      docs,
      deadline: null,
      loading: false,
    }),

    computed: {
      profileView: sync('app/views@profile'),
      user: sync('user'),
      status: sync('user/status'),
      toast: sync('app/toast'),
      help: sync('app/views@help'),
      help_sections: sync('help/sections'),

      completion () {
        return _.round(_.mean([this.step.profile, this.step.docs.completion]) * 100);
      },

      stepName () {
        return this.$options.name.toLowerCase();
      },

      late () {
        return this.$moment().isSameOrAfter(this.deadline);
      },

      actionRequired () {
        const status = this.status;
        return ['ACC', 'ENR'].includes(status);
      }
    },

    watch: {
      completion: { 
        immediate: true,
        handler (v) {
          this.step.cta.toggle = (v==100) && this.actionRequired;
        }
      },
      refresh (b) {
        if (b) this.getDocsStatus();
      }
    },

    methods: {
      ...services,

      actionCalled () {
        if (this.completion==100) {
          this.toggleConfirm(true);
        }
      },

      updateView () {
        this.getDocsStatus();
      },

      getDocInfo (doc) {
        if (doc==null) return null;
        const info = _.merge(_.clone(this.docs[doc]), this.step.docs.items[doc]);
        return info;
      },

      decodeWeek (text) {
        if (text.indexOf('week-')>=0) {
          const breakdown = text.split('week-');
          const n = parseInt(breakdown[1].substr(0, 1));
          const start = this.$moment().startOf('week').subtract(n, 'w').add(1, 'd');
          const end = this.$moment().endOf('week').subtract(n, 'w').add(1, 'd');
          const format = start.month()==end.month() ? 'DD' : 'DD/MM';
          const week = start.format(format) + ' - ' + end.format('DD/MM');
          return text.replace('week-'+n, week);
        }else{
          return text;
        }
      },

      docPending (status) {
        return status==0 || status==3
      },

      toggleDoc (b, doc=null) {
        setTimeout(vue => {
          vue.docView.selected = doc;
          vue.docView.toggle = b;
        }, 250, this);
      },

      toggleConfirm (b) {
        this.confirm.toggle = b;
      },

      updateDocsCompletion () {
        const docs = this.step.docs.items;
        const count = _.countBy(docs, doc => {
          return doc.status==1||doc.status==2;
        })[true];
        this.step.docs.completion = typeof count == 'undefined' ? 0 : count / _.size(docs);
      },

      getDocsStatus () {
        const token = this.user.auth.token;
        const cpf = this.rawCPF(this.user.cpf);
        this.loading = true;

        this.$api.get('/getprofileinfodoc/'+cpf+'/'+token+'/?format=json')
          .then(response => {
            console.log('getDocsStatus => ',response);
            if(response.data.retorno==200){
              let docs = _.keyBy(JSON.parse(response.data.documentos), 'criteria');

              const isOwner = this.user.profile.vehicle.isOwner;
              docs = _.mapValues(docs, (doc, d) => {
                if (d=='doc_rg_prop') {
                  doc.required = isOwner===null||isOwner===1 ? 0 : 1;
                }
                return doc;
              });

              this.step.docs.items = _.pickBy(docs, ['required', 1]);

              this.updateDocsCompletion();
              this.setStepText();

              this.deadline = this.$moment.utc(response.data.dt_Status).local().add(3, 'days').endOf('day');

              this.trackEvent('signup/'+this.stepName, 'get docs status ok');
            }else if(response.data.retorno==401) {
              this.getout();
              this.toggleToast(
                true,
                this.$t('errors.session_expired'),
                5000,
                false
              );
            }else{
              this.toggleToast(
                true,
                this.$t('signup.steps.ENR.errors.get'),
                5000,
                false,
              );
              this.trackEvent('signup/'+this.stepName, 'get docs status failed');
            }
          })
          .catch(error => {
            this.handleError(error);
          })
          .finally(() => {
            if (this.refresh) this.$emit('onRefresh');
            this.loading = false;
          });
      },

      sendDoc (photo) {
        const token = this.user.auth.token;
        const cpf = this.rawCPF(this.user.cpf);
        // this.docView.loading = true;
        this.docs[photo.doc].loading = true;
        console.log(photo);

        this.$api({
          url: '/senddocdriver',
            method: 'POST',
            data: {
              cpf: cpf,
              authTk: token,
              tipo: photo.doc,
              filename: photo.filename,
              file: photo.blob
            }
          })
          .then(response => {
            console.log('sendDoc => ',response);
            if(response.data.retorno==200){
              this.step.docs.items[photo.doc].url_midia = response.data.url_midia;
              this.step.docs.items[photo.doc].status = 1;

              this.updateDocsCompletion();

              const next = _.find(this.step.docs.items, doc => doc.status==0 || doc.status==3);
              if (!_.isNil(next)&&!this.docView.toggle) {
                setTimeout(() => {
                  this.toggleDoc(false);
                }, 250);
              }

              this.toggleToast(
                true,
                this.$t('signup.steps.ENR.saved'),
                3000,
                false,
              );

              this.trackEvent('signup/'+this.stepName, 'send doc ok');
            }else{
              this.toggleToast(
                true,
                this.$t('signup.steps.ENR.errors.send'),
                5000,
                false,
              );
              this.trackEvent('signup/'+this.stepName, 'send doc failed');
            }
          })
          .catch(error => {
            this.handleError(error, this.$t('errors.send_document_failed'), true);
          })
          .finally(() => {
            // this.docView.loading = false;
            this.docs[photo.doc].loading = false;
          });
      },

      requestDocEvaluation () {
        const token = this.user.auth.token;
        const cpf = this.rawCPF(this.user.cpf);
        this.confirm.loading = true;

        this.$api({
          url: '/requestdocevaluationdriver',
            method: 'POST',
            data: {
              cpf: cpf,
              authTk: token,
            }
          })
          .then(response => {
            console.log('requestDocEvaluation => ',response);
            if(response.data.retorno==200){
              this.step.cta.toggle = false;
              this.toggleConfirm(false);
              this.status = response.data.status_motorista;

              if (response.data.status_motorista=='FDB') {
                this.$emit('next', { status: this.status });
              }

              this.setStepText();

              this.toggleToast(
                true,
                this.$t('signup.steps.ENR.sent'),
                5000,
                false,
              );

              this.trackEvent('signup/'+this.stepName, 'request doc evaluation ok');
            }else{
              this.getDocsStatus();
              this.toggleToast(
                true,
                this.$t('signup.steps.ENR.errors.send'),
                5000,
                false,
              );
              this.trackEvent('signup/'+this.stepName, 'request doc evaluation failed');
            }
          })
          .catch(error => {
            this.handleError(error, this.$t('errors.send_documents_failed'), true);
          })
          .finally(() => {
            this.confirm.loading = false;
          });
      },

      setStepText () {
        if (this.status=='FDD') {
          this.step.title = 'signup.steps.ENR.documents_sent';
          this.step.subtitle = 'signup.steps.ENR.documents_sent_subtitle';
        }else{
          this.step.title = 'signup.steps.ENR.title';
          this.step.subtitle = 'signup.steps.ENR.subtitle';
        }
      }

    },

    created () {
    },

    mounted () {
      this.getDocsStatus();
    },

    directives: {
    },

  }
</script>
